import {IconButton} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {useCallback} from 'react';

import {isNil, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../../i18n/i18n';
import {PresetEditorDialog} from './components/PresetEditorDialog';
import {SavedFiltersDropdown} from './components/SavedFiltersDropdown';
import {usePresetEditor} from './hooks/usePresetEditor';
import {RelativeFilterPresetsController} from './hooks/useRelativeFilterPresetsController';

interface SavedFiltersProps extends TestIdProps {
  presetController: RelativeFilterPresetsController;
}

export function SavedFilters(props: SavedFiltersProps) {
  const {presetController} = props;

  const presetEditor = usePresetEditor(presetController, {
    deleteDialogTestId: suffixTestId('deletePresetDeleteDialog', props),
  });

  const handlePresetChange = useCallback(
    (presetId: string | null) => {
      if (isNil(presetId) || presetController.isCatalogueLoading) {
        return;
      }
      const found = presetController.presets.find((preset) => preset.id === presetId);
      if (isNil(found)) {
        return;
      }
      presetController.setActivePresetId(presetId);
      presetController.applyPreset(found.config);
    },
    [presetController]
  );

  const handleCreateOption = useCallback(() => {
    presetEditor.openCreate();
  }, [presetEditor]);

  return (
    <>
      <HStack align="flex-end" spacing={1}>
        <Box flexGrow={1} minWidth={0}>
          <SavedFiltersDropdown
            presetController={presetController}
            label={i18n.t('entity.filterForm.savedFilters.label')}
            placeholder={i18n.t('entity.filterForm.savedFilters.placeholder')}
            onSelectPreset={handlePresetChange}
            onCreatePreset={handleCreateOption}
            data-testid={suffixTestId('savedFiltersDropdown', props)}
          />
        </Box>
        <IconButton
          icon="action/settings"
          priority="tertiary"
          isDisabled={isNil(presetController.displayedPresetId)}
          onClick={() => {
            if (isNotNil(presetController.displayedPresetId)) {
              presetEditor.openEdit(presetController.displayedPresetId);
            }
          }}
          aria-label={i18n.t('entity.filterForm.savedFilters.editPreset')}
          data-testid={suffixTestId('editPresetButton', props)}
        />
      </HStack>

      <PresetEditorDialog
        key={presetEditor.dialogKey}
        isOpen={presetEditor.isOpen}
        initialPreset={presetEditor.dialogPreset}
        onSave={presetEditor.handleSave}
        onDelete={
          presetEditor.dialogState.mode === 'edit' ? presetEditor.handleDeletePress : undefined
        }
        onClose={presetEditor.close}
        data-testid={suffixTestId('presetEditor', props)}
      />
    </>
  );
}
